// Modele de donnees de la vue acheteur operation-MS (v2).
// Source unique des codes de statut : table `status` du seeder
// subsequent/api/database/seeders/DatabaseSeeder.php.
// Gating sur l'id, pas sur le nom (les libelles DB sont volatils).

// PROCESS ACTUEL (les ids ne suivent PAS l'ordre du cycle de vie) :
//   Operation : 1 Brouillon -> 2 Consultation -> 4 A analyser -> 13 A signer -> 11 Valide.
//              Annulation avant signature -> 14.
//   Commande  : 3 A candidater -> 5 candidate (et retenue) | 6 refuse/non-retenue | 12 expire ;
//              retenue apres signature -> 11.
//   13 = "A signer" du mode signature MANUSCRITE (OTP + dessin). 7/8/9/10 = ballet de signature
//   du mode AUTOMATIQUE (acheteur puis prestataire) : NON morts, vivants selon le mode renvoye
//   par GET /app/signature-mode. Ne pas les traiter comme obsoletes.
export enum StatusId {
  Brouillon = 1, // Systeme
  ConsultationEnCours = 2, // Acheteur — consultation lancee (ouvre Q&R + Analyse verrouillee)
  ACandidater = 3, // Commande — candidature attendue
  AAnalyser = 4, // Acheteur — toutes reponses recues, dechiffrage / analyse
  EnAttenteAnalyse = 5, // Commande — candidate (et retenue apres cloture)
  Refuser = 6, // refus (presta qui refuse l'offre, OU candidature non-retenue)
  ASigner = 7, // Mode automatique : acheteur a signer
  EnAttenteSignaturePrestataire = 8, // Mode automatique : attente signature prestataire
  EnAttenteSignatureAcheteur = 9, // Mode automatique : attente signature acheteur
  ASignerPrestataire = 10, // Mode automatique : prestataire a signer
  Valider = 11, // signe (operation ET commande retenue) — ouvre la Messagerie
  Expirer = 12, // expiration (commande sans reponse dans le delai ; job d'expiration)
  ANotifier = 13, // Acheteur — "A signer" (mode manuscrite) : analyse cloturee, attend signature
  Annule = 14, // annulation (toutes commandes + operation)
}

// Statut de l'analyse IA des offres (pre-remplissage acheteur). Cf. operation.analyse_ia_statut.
export type AnalyseIaStatut = 'none' | 'en_cours' | 'terminee' | 'terminee_partielle' | 'erreur';

// Onglets acheteur MS (cf. seeder getTabs : 7 onglets cote client).
export type TabKind =
  | 'historique'
  | 'consultation'
  | 'questions_reponses'
  | 'analyse'
  | 'notifications'
  | 'messagerie'
  | 'evaluation';

export interface TabDescriptor {
  key: string;
  kind: TabKind;
  label: string;
  disabled: boolean;
  inactive: boolean;
}

export interface StatusRef {
  id?: number;
  nom?: string;
}

// Sac de formulaire MS (front) : champs connus + reste libre.
export interface MsFormulaire {
  nomOuvrage?: string;
  nomOperation?: string;
  referenceInterne?: string;
  besoin?: string;
  delaisConsultation?: number | string;
  delaisFinOperation?: number | string;
  visitePreavis?: boolean | number | string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  coordonneesMaitrise?: string;
  coordonneesMaitriseDOuvrage?: string;
  conditionVisite?: string;
  attestationBPU?: boolean;
  // Descriptif administratif — sous-bloc Operation (refonte PDF). Champs front ; persistance backend a venir.
  typeCommande?: string;
  pays?: string;
  phases?: string;
  moeInterlocuteur?: string;
  moeMail?: string;
  moeTelephone?: string;
  // Descriptif administratif — bloc Acheteur : seuls champs SAISIS (le reste du bloc = recap read-only).
  signataire?: string;
  modaliteFacturation?: string;
  // Contact operationnel de CETTE operation (saisi, pre-rempli avec l'acheteur qui l'initie).
  contactNom?: string;
  contactFonction?: string;
  contactMail?: string;
  contactTelephone?: string;
  // Descriptif administratif — sous-bloc Particularites administratives (refonte xlsx).
  niveauSensibilite?: string; // menu deroulant : Public (P) / Diffusion restreinte (DR) / Secret (S) / Tres Secret (TS)
  visiteObligatoire?: string;
  verificationIdentites?: string;
  habilitation?: string;
  charteReglementation?: string;
  clauseSuiviEnvironnemental?: string;
  // Descriptif technique (refonte xlsx) — champs documentaires editables (optionnels).
  urlDocTechnique?: string;
  modaliteAccesDocumentation?: string;
  listePiecesDocumentation?: string;
  // Descriptif procedural (refonte xlsx) — champs SAISIS par l'acheteur, persistes (colonnes operation).
  // Read-only ailleurs (duree, module Q/R, lots = derives).
  // dateLimiteReceptionOffres : pre-remplie avec la valeur deduite de la duree mais MODIFIABLE.
  dateLimiteReceptionOffres?: string;
  dateOuverturePlis?: string;
  dateNotification?: string;
  budgetMaximum?: string;
  origineFinancement?: string;
  sections?: Array<{ articles?: unknown[]; [key: string]: unknown }>;
  dateDebutConsultation?: string;
  dateDebutOperation?: string;
  dateFinOperation?: string;
  [key: string]: unknown;
}

export interface HistoriqueEvent {
  prefix?: string;
  type?: string;
  label?: string | null;
  message?: string;
  operateur?: string | null;
  destinataire?: number | string | null;
  ip_address?: string | null;
  created_at?: string;
  updated_at?: string;
}

// Commande = candidature d'un prestataire rattachee a l'operation MS.
export interface CommandeMS {
  id?: number;
  id_status?: number;
  id_entite?: number;
  status?: StatusRef;
  [key: string]: unknown;
}

// Entite operation-MS telle que consommee par la vue acheteur. Champs declares
// explicitement ; l'index signature reste un filet `unknown` (jamais `any`).
export interface OperationMS {
  id: number;
  numero?: string | number;
  nom?: string;
  nom_ouvrage?: string;
  // Contact operationnel porte par l'OPERATION (colonnes contact_* ajoutees en base), distinct du
  // compte acheteur et de la fiche organisme.
  contact_nom?: string;
  contact_fonction?: string;
  contact_mail?: string;
  contact_telephone?: string;
  // Descriptif au niveau colonnes (source apres sauvegarde ; le `formulaire` est le buffer d'edition).
  reference?: string;
  besoin?: string;
  delais?: number | string;
  delais_fin_operation?: number | string;
  visite?: boolean | number | string;
  adresse?: string;
  code_postal?: string;
  ville?: string;
  coordonnees_maitrise?: string;
  coordonnees_maitrise_ouvrage?: string;
  conditionVisite?: string;
  // Descriptif administratif (refonte Consultation MS) — colonnes editables renvoyees par le GET.
  pays?: string;
  moe_interlocuteur?: string;
  moe_mail?: string;
  moe_telephone?: string;
  phases?: string;
  signataire_commande?: string;
  modalite_facturation?: string;
  niveau_sensibilite?: string;
  visite_obligatoire?: string;
  verification_identites?: string;
  habilitation?: string;
  charte_reglementation?: string;
  clause_suivi_environnemental?: string;
  // Descriptif technique (refonte Consultation MS) — colonnes documentaires renvoyees par le GET.
  url_doc_technique?: string;
  modalite_acces_documentation?: string;
  liste_pieces_documentation?: string;
  // Descriptif procedural (refonte Consultation MS) — colonnes saisies renvoyees par le GET.
  date_limite_reception_offres?: string;
  date_ouverture_plis?: string;
  date_notification?: string;
  budget_maximum?: string;
  origine_financement?: string;
  sections?: Array<{ articles?: unknown[]; [key: string]: unknown }>;
  reponse_technique?: string | null;
  id_status?: number;
  status?: StatusRef;
  id_commande?: number;
  id_signataire?: number;
  type_signataire?: string;
  acheteur_signataire?: { id_user?: number | string };
  // CREATEUR de l'operation, injecte par le core depuis id_user + type_compte (acheteur,
  // responsable_achat ou validateur). C'est « la personne qui commence » — a ne pas confondre avec
  // organisme.acheteur (acheteur par defaut de l'organisme).
  acheteur?: {
    id?: number | string;
    nom?: string;
    prenom?: string;
    email_contact?: string;
    telephone?: string;
    fonction?: string;
  };
  transferred?: boolean;
  commandes?: CommandeMS[];
  historique?: HistoriqueEvent[];
  lot_technique?: { id?: number | string; nom?: string };
  id_lot_technique?: number;
  id_lot_departement?: number;
  lot_departement?: { id_departement?: number; departement?: { id?: number | string; nom?: string } };
  // Recap acheteur (LECTURE SEULE) du Descriptif administratif : affiche tel quel, jamais renvoye
  // dans le payload. La plupart de ces champs ne sont pas encore exposes par le payload subsequent
  // -> ils s'afficheront des que le backend enrichira l'organisme de l'operation.
  organisme?: {
    id?: number;
    nom?: string;
    siret?: string;
    type_organisme?: string;
    // adresse = entite imbriquee (PAS une chaine) : champs de voie + ville/cp/pays.
    adresse?: {
      numero_voie?: string | number;
      type_voie?: string;
      nom_voie?: string;
      code_postal?: string;
      ville?: string;
      pays?: string;
      [key: string]: unknown;
    };
    modalites_facturation?: string;
    contact_operationnel?: { nom?: string; fonction?: string; mail?: string; telephone?: string };
    signataire?: { nom?: string };
    acheteur?: { prenom?: string; nom?: string; telephone?: string; email_contact?: string; fonction?: string };
  };
  expire_at?: string;
  date_limite_remise?: string;
  date_debut_operation?: string;
  date_fin_operation?: string;
  created_at?: string;
  updated_at?: string;
  cree_par?: string | null;
  formulaire?: MsFormulaire;
  analyse_ia_statut?: AnalyseIaStatut;
  analyse_ia_at?: string | null;
  analyse_ia_erreurs?: Array<{ commande_id?: number; code?: string }> | string[] | null;
  [key: string]: unknown;
}
